/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/event_statuses-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-event_statuses-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/event_statuses-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, name
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/event_statuses-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-event_statuses-api-client.ts (API client)
 * - client/api/generated_schemas/event_statuses.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-09-15T20:50:53.139Z
 * Source config: config/entities/event_statuses-entity-config.ts
 * Public fields: 7/7 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EventStatusesClient, EventStatusesQueryOptions } from '../generated_clients/generated-event_statuses-api-client.js';
import {
  EventStatusesInsert,
  EventStatusesUpdate,
  EventStatusesFieldConfig
} from '../../types/generated_schemas/generated-event_statuses-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/event_statuses_business_hooks/

// Query keys with enhanced caching strategy
export const eventStatusesKeys = {
  all: ['event_statuses'] as const,
  lists: () => [...eventStatusesKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...eventStatusesKeys.lists(), { filters }] as const,
  details: () => [...eventStatusesKeys.all, 'detail'] as const,
  detail: (id: string) => [...eventStatusesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...eventStatusesKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...eventStatusesKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...eventStatusesKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...eventStatusesKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useEventStatusesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new EventStatusesClient();
};

/**
 * Hook to get all event_statuses with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with event_statuses data
 */
export const useEventStatusesList = (options?: EventStatusesQueryOptions) => {
  const client = useEventStatusesClient();
  
  return useQuery({
    queryKey: eventStatusesKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single event_statuse by ID
 *
 * @param id - The event_statuse ID
 * @returns React Query result with single event_statuse data
 */
export const useEventStatuses = (id: string) => {
  const client = useEventStatusesClient();
  
  return useQuery({
    queryKey: eventStatusesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/event_statuses
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/event_statuses_business_hooks/
 */

/**
 * Hook to create a new event_statuse
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, name
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating event_statuse
 */
export const useCreateEventStatuses = () => {
  const client = useEventStatusesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: EventStatusesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventStatusesKeys.all });
    },
  });
};

/**
 * Hook to update an existing event_statuse
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating event_statuse
 */
export const useUpdateEventStatuses = () => {
  const client = useEventStatusesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EventStatusesUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: eventStatusesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: eventStatusesKeys.lists() });
    },
  });
};

/**
 * Hook to delete a event_statuse
 *
 * @returns React Query mutation for deleting event_statuse
 */
export const useDeleteEventStatuses = () => {
  const client = useEventStatusesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventStatusesKeys.all });
    },
  });
};

/**
 * Hook to count event_statuses with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useEventStatusesCount = (filter?: Partial<Record<typeof EventStatusesFieldConfig.filterable[number], any>>) => {
  const client = useEventStatusesClient();
  
  return useQuery({
    queryKey: eventStatusesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useEventStatusesFieldConfig = () => {
  return EventStatusesFieldConfig;
};